import React, { useState, useEffect } from 'react'
import { XMarkIcon, EyeIcon, EyeSlashIcon, KeyIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { toast } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { authApi } from '@/services/api'
import { useAuth } from '@/contexts/AuthContext'
import { PasswordStrengthIndicator } from '@/components/auth/PasswordStrengthIndicator'
import { PasswordRequirementsList } from '@/components/auth/PasswordRequirementsList'
import { validatePassword, checkPasswordsMatch } from '@/utils/passwordValidation'
import { usePasswordPolicy } from '@/hooks/usePasswordPolicy'

interface PasswordChangeModalProps {
  isOpen: boolean
  onClose: () => void
  isForced?: boolean
  reason?: string
  onSuccess?: () => void
}

const PasswordChangeModal: React.FC<PasswordChangeModalProps> = ({
  isOpen,
  onClose,
  isForced = false,
  reason,
  onSuccess
}) => {
  const { t } = useTranslation(['toasts', 'auth', 'common'])
  const { updateToken, user } = useAuth()
  // Pass the logged-in user's identity so the always-on "must not contain your
  // name/email" rule is enforced live in the checklist (and validateForm),
  // mirroring the backend containsUserData() check.
  const { requirements: passwordRequirements } = usePasswordPolicy(
    user
      ? { email: user.email, firstName: user.firstName, lastName: user.lastName }
      : undefined,
  )
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  })
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      })
      setErrors({})
    }
  }, [isOpen])

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    // Validate current password (skip for forced changes from OAuth)
    if (!isForced && !formData.currentPassword) {
      newErrors.currentPassword = t('auth:passwordChange.currentRequired', 'Current password is required')
    }

    // Validate new password using utility
    const passwordValidation = validatePassword(
      formData.newPassword,
      formData.currentPassword,
      passwordRequirements
    )

    if (!passwordValidation.isValid) {
      newErrors.newPassword = passwordValidation.errors[0] || t('auth:passwordChange.doesNotMeet', 'Password does not meet requirements')
    }

    // Validate password confirmation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('auth:passwordChange.confirmRequired', 'Please confirm your new password')
    } else if (!checkPasswordsMatch(formData.newPassword, formData.confirmPassword)) {
      newErrors.confirmPassword = t('auth:passwordChange.mismatch', 'Passwords do not match')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setLoading(true)
    try {
      const response = await authApi.changePassword({
        current_password: formData.currentPassword,
        new_password: formData.newPassword,
      })

      if (response.success) {
        // Update the token with the new access token (without forcePasswordChange flag)
        if (response.data?.access_token) {
          updateToken(response.data.access_token)
        }

        toast.success(t('success.passwordChanged'))
        onSuccess?.()
        onClose()
      } else {
        toast.error(response.errors?.[0]?.message || t('error.passwordChangeFailed'))
      }
    } catch (error: any) {
      console.error('Password change error:', error)

      // The apiClient rejects with the response body (see client.ts:
      // `Promise.reject(error.response?.data || error)`), so `error.code` and
      // `error.details` are reachable directly. `details` is an array of the
      // SPECIFIC backend reasons (e.g. "Password is too common"). Prefer that
      // specific reason over the generic toast so the user learns WHY the
      // password was rejected (e.g. the always-on common/user-data rules that
      // aren't part of the admin policy). Defensive `error.response?.data`
      // fallback in case a caller passes a raw axios error.
      const code = error.code ?? error.response?.data?.code
      const detailsRaw = error.details ?? error.response?.data?.details
      const specificReason = Array.isArray(detailsRaw)
        ? detailsRaw[0]
        : typeof detailsRaw === 'string'
          ? detailsRaw
          : undefined

      // Handle specific error codes
      if (code === 'INVALID_CURRENT_PASSWORD') {
        setErrors({ currentPassword: t('error.currentPasswordIncorrect') })
        toast.error(t('error.currentPasswordIncorrect'))
      } else if (code === 'WEAK_PASSWORD') {
        const message = specificReason || t('error.weakPassword')
        setErrors({ newPassword: message })
        toast.error(message)
      } else if (code === 'PASSWORD_REUSE') {
        setErrors({ newPassword: t('error.passwordReuse') })
        toast.error(t('error.passwordReuseDetails'))
      } else if (code === 'RATE_LIMIT_EXCEEDED') {
        toast.error(t('error.rateLimitExceeded'))
      } else {
        toast.error(specificReason || error.error || error.message || t('error.passwordChangeFailed'))
      }
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field: 'currentPassword' | 'newPassword' | 'confirmPassword', value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }))
  }

  if (!isOpen) return null

  const canClose = !isForced

  return (
    <div className="fixed inset-0 bg-neutral-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
      <div className="relative mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-lg bg-white max-h-[90vh] overflow-y-auto">
        <div>
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="flex items-center justify-center h-12 w-12 rounded-full bg-primary-100">
                <KeyIcon className="h-6 w-6 text-primary-600" />
              </div>
              <h3 className="ms-3 text-xl font-semibold text-neutral-900">
                {isForced ? t('auth:passwordChange.titleForced', 'Password Change Required') : t('auth:passwordChange.title', 'Change Password')}
              </h3>
            </div>
            {canClose && (
              <button
                onClick={onClose}
                className="text-neutral-400 hover:text-neutral-600 transition-colors"
                aria-label={t('auth:passwordChange.close', 'Close modal')}
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            )}
          </div>

          {/* Force password change alert */}
          {isForced && (
            <div className="mb-6 p-4 bg-secondary-50 border-s-4 border-secondary-500 rounded-md">
              <div className="flex items-start">
                <ExclamationTriangleIcon className="h-5 w-5 text-secondary-600 mt-0.5 me-3 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-secondary-900">
                    {t('auth:passwordChange.requiredBanner', 'Password change required')}
                  </p>
                  <p className="text-sm text-secondary-700 mt-1">
                    {reason || t('auth:passwordChange.requiredReason', 'You must change your password before continuing to use the application.')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left column - Form inputs */}
              <div className="space-y-4">
                {!isForced && (
                  <div>
                    <label htmlFor="currentPassword" className="block text-sm font-medium text-neutral-700 mb-2">
                      {t('auth:passwordChange.current', 'Current Password')}
                    </label>
                    <div className="relative">
                      <input
                        type={showPasswords.current ? 'text' : 'password'}
                        id="currentPassword"
                        value={formData.currentPassword}
                        onChange={(e) => handleInputChange('currentPassword', e.target.value)}
                        className={`block w-full px-3 py-2 pe-10 border rounded-md shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                          errors.currentPassword ? 'border-error-300' : 'border-neutral-300'
                        }`}
                        placeholder={t('auth:passwordChange.currentPlaceholder', 'Enter current password')}
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('current')}
                        className="absolute inset-y-0 end-0 pe-3 flex items-center hover:text-neutral-600"
                        aria-label={showPasswords.current ? t('auth:passwordChange.hide', 'Hide password') : t('auth:passwordChange.show', 'Show password')}
                      >
                        {showPasswords.current ? (
                          <EyeSlashIcon className="h-5 w-5 text-neutral-400" />
                        ) : (
                          <EyeIcon className="h-5 w-5 text-neutral-400" />
                        )}
                      </button>
                    </div>
                    {errors.currentPassword && (
                      <p className="mt-2 text-sm text-error-600">{errors.currentPassword}</p>
                    )}
                  </div>
                )}

                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-neutral-700 mb-2">
                    {t('auth:passwordChange.new', 'New Password')}
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.new ? 'text' : 'password'}
                      id="newPassword"
                      value={formData.newPassword}
                      onChange={(e) => handleInputChange('newPassword', e.target.value)}
                      className={`block w-full px-3 py-2 pe-10 border rounded-md shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                        errors.newPassword ? 'border-error-300' : 'border-neutral-300'
                      }`}
                      placeholder={t('auth:passwordChange.newPlaceholder', 'Enter new password')}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('new')}
                      className="absolute inset-y-0 end-0 pe-3 flex items-center hover:text-neutral-600"
                      aria-label={showPasswords.new ? t('auth:passwordChange.hide', 'Hide password') : t('auth:passwordChange.show', 'Show password')}
                    >
                      {showPasswords.new ? (
                        <EyeSlashIcon className="h-5 w-5 text-neutral-400" />
                      ) : (
                        <EyeIcon className="h-5 w-5 text-neutral-400" />
                      )}
                    </button>
                  </div>
                  {errors.newPassword && (
                    <p className="mt-2 text-sm text-error-600">{errors.newPassword}</p>
                  )}

                  {/* Password strength indicator */}
                  <div className="mt-3">
                    <PasswordStrengthIndicator password={formData.newPassword} />
                  </div>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-neutral-700 mb-2">
                    {t('auth:passwordChange.confirm', 'Confirm New Password')}
                  </label>
                  <div className="relative">
                    <input
                      type={showPasswords.confirm ? 'text' : 'password'}
                      id="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      className={`block w-full px-3 py-2 pe-10 border rounded-md shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
                        errors.confirmPassword ? 'border-error-300' : 'border-neutral-300'
                      }`}
                      placeholder={t('auth:passwordChange.confirmPlaceholder', 'Confirm new password')}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('confirm')}
                      className="absolute inset-y-0 end-0 pe-3 flex items-center hover:text-neutral-600"
                      aria-label={showPasswords.confirm ? t('auth:passwordChange.hide', 'Hide password') : t('auth:passwordChange.show', 'Show password')}
                    >
                      {showPasswords.confirm ? (
                        <EyeSlashIcon className="h-5 w-5 text-neutral-400" />
                      ) : (
                        <EyeIcon className="h-5 w-5 text-neutral-400" />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="mt-2 text-sm text-error-600">{errors.confirmPassword}</p>
                  )}
                </div>
              </div>

              {/* Right column - Password requirements */}
              <div className="bg-neutral-50 p-4 rounded-lg border border-neutral-200">
                <PasswordRequirementsList password={formData.newPassword} />
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 rtl:space-x-reverse pt-6 border-t border-neutral-200">
              {canClose && (
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-2.5 border border-neutral-300 text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                  disabled={loading}
                >
                  {t('common:cancel', 'Cancel')}
                </button>
              )}
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ms-1 me-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {t('auth:passwordChange.changing', 'Changing...')}
                  </span>
                ) : t('auth:passwordChange.title', 'Change Password')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default PasswordChangeModal
